// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Public Pages
import Home from "./pages/Home";
import Login from "./pages/Login";

// Admin Pages
import Dashboard from "./pages/Dashboard";
import Banks from "./pages/Banks";
import Authorizations from "./pages/Authorizations";
import Transactions from "./pages/Transactions";
import Pricing from "./pages/Pricing";
import Settlements from "./pages/Settlements";
import SettlementAccount from "./pages/SettlementAccount";
import FeeSchedules from "./pages/FeeSchedules";
import SettlementLedger from "./pages/SettlementLedger";
import AuditLogs from "./pages/AuditLogs";
import Webhooks from "./pages/Webhooks";
import Settings from "./pages/Settings";
import Invoices from "./pages/Invoices";
import Tier3Operators from "./pages/Tier3Operators";
import RetailStores from "./pages/RetailStores";
import Simulator from "./pages/Simulator";
import AtmDeposit from "./pages/AtmDeposit";
import Deposits from "./pages/Deposits";
import DepositLimits from "./pages/DepositLimits";
import RailAdapters from "./pages/RailAdapters";
import Certificates from "./pages/Certificates";
import Layout from "./components/Layout";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          
          {/* Admin Portal Routes */}
          <Route path="/admin/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="banks" element={<Banks />} />
            <Route path="retail-stores" element={<RetailStores />} />
            <Route path="atm-acquirers" element={<Tier3Operators />} />
            <Route path="tier3-operators" element={<Tier3Operators />} />
            <Route path="authorizations" element={<Authorizations />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="fee-schedules" element={<FeeSchedules />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="settlements" element={<Settlements />} />
            <Route path="settlement-ledger" element={<SettlementLedger />} />
            <Route path="settlement-account" element={<SettlementAccount />} />
            <Route path="webhooks" element={<Webhooks />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="simulator" element={<Simulator />} />
            <Route path="atm-deposit" element={<AtmDeposit />} />
            <Route path="deposits" element={<Deposits />} />
            <Route path="deposit-limits" element={<DepositLimits />} />
            <Route path="rails" element={<RailAdapters />} />
            <Route path="settings" element={<Settings />} />
            <Route path="certificates" element={<Certificates />} />
          </Route>
          
          {/* Legacy redirects - redirect old paths to new admin paths */}
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/banks" element={<Navigate to="/admin/banks" replace />} />
          <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/transactions" element={<Navigate to="/admin/transactions" replace />} />
          <Route path="/simulator" element={<Navigate to="/admin/simulator" replace />} />
          <Route path="/settlements" element={<Navigate to="/admin/settlements" replace />} />
          <Route path="/pricing" element={<Navigate to="/admin/pricing" replace />} />
          <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
          
          {/* Catch all - admin paths stay in admin, others go to home */}
          <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
